import type { MessageValue, NoMessageValues } from 'react-intl'
import { defineMessages } from 'react-intl'

export const reminderMessages = defineMessages<{
  readonly actionSavedReminderOpen: NoMessageValues
  readonly agendaEmpty: NoMessageValues
  readonly agendaMore: { readonly shown: MessageValue; readonly total: MessageValue }
  readonly bookedBy: { readonly name: MessageValue }
  readonly cancelReminder: NoMessageValues
  readonly cancelled: NoMessageValues
  readonly closeWarning: { readonly count: number | bigint }
  readonly completed: NoMessageValues
  readonly created: NoMessageValues
  readonly dayCount: { readonly count: number | bigint }
  readonly done: NoMessageValues
  readonly doneWithoutLogging: NoMessageValues
  readonly dueAtLabel: NoMessageValues
  readonly dueBody: { readonly time: MessageValue }
  readonly dueBurstBody: NoMessageValues
  readonly dueBurstTitle: { readonly count: number | bigint }
  readonly dueInPast: NoMessageValues
  readonly dueTitle: { readonly code: MessageValue }
  readonly editReminder: NoMessageValues
  readonly jakartaTime: NoMessageValues
  readonly loadFailed: NoMessageValues
  readonly moreActions: NoMessageValues
  readonly notOpenAnymore: NoMessageValues
  readonly noteLabel: NoMessageValues
  readonly noteTooLong: { readonly max: MessageValue }
  readonly open: NoMessageValues
  readonly openCase: NoMessageValues
  readonly outsideHours: NoMessageValues
  readonly outsideHoursWarning: NoMessageValues
  readonly outsideHoursWarningNextOpening: { readonly nextOpening: MessageValue }
  readonly overdue: NoMessageValues
  readonly overdueTitle: NoMessageValues
  readonly pageTitle: NoMessageValues
  readonly pickTime: NoMessageValues
  readonly presetPlusOneHour: NoMessageValues
  readonly presetPlusThreeHours: NoMessageValues
  readonly presetTomorrowMorning: NoMessageValues
  readonly remindersTitle: NoMessageValues
  readonly rescheduled: NoMessageValues
  readonly retry: NoMessageValues
  readonly save: NoMessageValues
  readonly scopeAll: NoMessageValues
  readonly scopeMine: NoMessageValues
  readonly scopeTeam: NoMessageValues
  readonly snooze: NoMessageValues
  readonly snoozeUntil: NoMessageValues
  readonly typeAny: NoMessageValues
  readonly typeLabel: NoMessageValues
  readonly unassigned: NoMessageValues
  readonly viewReminders: NoMessageValues
}>({
  actionSavedReminderOpen: {
    defaultMessage: 'Action saved, but the reminder is still open.',
    description: 'case reminder done flow — action logged, completing the reminder failed',
  },
  agendaEmpty: {
    defaultMessage: 'No reminders on this day.',
    description: 'reminders page — day agenda empty state',
  },
  agendaMore: {
    defaultMessage: 'Showing the first {shown} of {total}.',
    description: 'reminders page — the list is capped',
  },
  bookedBy: {
    defaultMessage: 'Booked by {name}',
    description: 'case reminder — who booked it (an operator, or the intake bot)',
  },
  cancelReminder: {
    defaultMessage: 'Cancel reminder',
    description: 'case reminder row action',
  },
  cancelled: {
    defaultMessage: 'Reminder cancelled.',
    description: 'case reminder toast',
  },
  closeWarning: {
    defaultMessage:
      '{count, plural, one {This case has an open reminder. It will be cancelled.} other {This case has # open reminders. They will be cancelled.}}',
    description: 'close case dialog — open reminders will be cancelled',
  },
  completed: {
    defaultMessage: 'Reminder done.',
    description: 'case reminder toast',
  },
  created: {
    defaultMessage: 'Reminder set.',
    description: 'case reminder toast',
  },
  dayCount: {
    defaultMessage: '{count, plural, one {# reminder} other {# reminders}}',
    description: 'reminders calendar — accessible count on a day',
  },
  done: {
    defaultMessage: 'Done',
    description: 'case reminder row action — log the action that finishes it',
  },
  doneWithoutLogging: {
    defaultMessage: 'Done without logging',
    description: 'case reminder row action — mark done, log no action',
  },
  dueAtLabel: {
    defaultMessage: 'When',
    description: 'case reminder form — due time field',
  },
  dueBody: {
    defaultMessage: 'Due {time}',
    description: 'reminder due alert body — the due time in Jakarta',
  },
  dueBurstBody: {
    defaultMessage: 'Open the Reminders page to see them.',
    description: 'reminder due alert — several came due together',
  },
  dueBurstTitle: {
    defaultMessage: '{count, plural, one {# reminder due} other {# reminders due}}',
    description: 'reminder due alert — several came due together',
  },
  dueInPast: {
    defaultMessage: 'Pick a time in the future.',
    description: 'case reminder form — due time in the past',
  },
  dueTitle: {
    defaultMessage: 'Reminder due: {code}',
    description: 'reminder due alert title — one reminder, the case code',
  },
  editReminder: {
    defaultMessage: 'Edit reminder',
    description: 'case reminder row action and dialog title',
  },
  jakartaTime: {
    defaultMessage: 'Jakarta time (WIB)',
    description: 'case reminder form — the time zone the picker works in',
  },
  loadFailed: {
    defaultMessage: "Couldn't load reminders.",
    description: 'reminders page — a query failed',
  },
  moreActions: {
    defaultMessage: 'More reminder actions',
    description: 'case reminder row — overflow menu button',
  },
  notOpenAnymore: {
    defaultMessage: 'This reminder is already closed.',
    description: 'case reminder done flow — the reminder was finished or cancelled meanwhile',
  },
  noteLabel: {
    defaultMessage: 'Note',
    description: 'case reminder form — note field',
  },
  noteTooLong: {
    defaultMessage: 'At most {max} characters.',
    description: 'case reminder form — note too long',
  },
  open: {
    defaultMessage: 'Open',
    description: 'reminder row action — open the case',
  },
  openCase: {
    defaultMessage: 'Open case',
    description: 'reminder due toast button',
  },
  outsideHours: {
    defaultMessage: 'Outside hours',
    description: 'case reminder chip — the due time is outside business hours',
  },
  outsideHoursWarning: {
    defaultMessage: 'This is outside business hours. You can still keep it.',
    description: 'case reminder form — the chosen time is outside business hours',
  },
  outsideHoursWarningNextOpening: {
    defaultMessage:
      'This is outside business hours; the office next opens {nextOpening}. You can still keep it.',
    description: 'case reminder form — the chosen time is outside business hours',
  },
  overdue: {
    defaultMessage: 'Overdue',
    description: 'case reminder chip',
  },
  overdueTitle: {
    defaultMessage: 'Overdue',
    description: 'reminders page — card of overdue reminders across days',
  },
  pageTitle: {
    defaultMessage: 'Reminders',
    description: 'reminders page title',
  },
  pickTime: {
    defaultMessage: 'Pick a time…',
    description: 'case reminder snooze menu — open a date-time picker',
  },
  presetPlusOneHour: {
    defaultMessage: '+1 h',
    description: 'case reminder preset — one hour from now',
  },
  presetPlusThreeHours: {
    defaultMessage: '+3 h',
    description: 'case reminder preset — three hours from now',
  },
  presetTomorrowMorning: {
    defaultMessage: 'Tomorrow 09:00',
    description: 'case reminder preset — tomorrow at 09:00',
  },
  remindersTitle: {
    defaultMessage: 'Upcoming',
    description: 'case detail card title — open reminders',
  },
  rescheduled: {
    defaultMessage: 'Reminder moved.',
    description: 'case reminder toast',
  },
  retry: {
    defaultMessage: 'Retry',
    description: 'case reminder done flow — retry completing the reminder',
  },
  save: {
    defaultMessage: 'Set reminder',
    description: 'case reminder form — save button',
  },
  scopeAll: {
    defaultMessage: 'All',
    description: 'reminders page tab — every case',
  },
  scopeMine: {
    defaultMessage: 'Mine',
    description: 'reminders page tab — my cases',
  },
  scopeTeam: {
    defaultMessage: 'Team',
    description: 'reminders page tab — my team cases',
  },
  snooze: {
    defaultMessage: 'Snooze',
    description: 'case reminder row action',
  },
  snoozeUntil: {
    defaultMessage: 'Snooze until',
    description: 'case reminder pick dialog title',
  },
  typeAny: {
    defaultMessage: 'Any',
    description: 'case reminder form — no follow-up type chosen',
  },
  typeLabel: {
    defaultMessage: 'Follow up by',
    description: 'case reminder form — action type field',
  },
  unassigned: {
    defaultMessage: 'Unassigned',
    description: 'reminder row chip — the case has no assignee',
  },
  viewReminders: {
    defaultMessage: 'View reminders',
    description: 'reminder due toast button — several reminders',
  },
})
